<template>
    <div>
        <toast />
        <!--
            Specialised toast for the "buyer missing Two contact details"
            error path. The custom #message template lets us render a
            router-link to the buyer's edit dialog (focused on the Two
            Billing Details section) so MoR users can jump straight to
            the missing fields instead of having to find the buyer
            manually. Triggered from TwoOrderSection.vue via
            `toast.add({ group: "two-config-error", data: { buyerLink } })`.
        -->
        <toast group="two-config-error" position="top-right">
            <template #message="slotProps">
                <div class="tw-flex tw-items-start tw-gap-2 tw-min-w-0">
                    <i
                        class="pi pi-exclamation-circle tw-text-tertiary-red tw-mt-0.5"
                    />
                    <div class="tw-flex tw-flex-col tw-gap-1 tw-min-w-0">
                        <span class="tw-font-semibold">{{
                            slotProps.message.summary
                        }}</span>
                        <span class="tw-text-sm tw-text-middleGrey">{{
                            slotProps.message.detail
                        }}</span>
                        <!-- `tw-mt-2` (on top of the parent's `tw-gap-1`)
                             gives ~12px between this CTA and the BE detail
                             line above, so the two paragraphs read as
                             separate blocks rather than running together. -->
                        <span class="tw-text-sm tw-text-middleGrey tw-mt-2">
                            Please edit the buyer details to add the required
                            information on the
                            <RouterLink
                                v-if="slotProps.message.data?.buyerLink"
                                :to="slotProps.message.data.buyerLink"
                                class="tw-text-primary tw-font-semibold hover:tw-underline"
                                data-testid="two_config_error_toast_link"
                                >buyer details page</RouterLink
                            >.
                        </span>
                    </div>
                </div>
            </template>
        </toast>
        <RouterView v-if="!shouldBlockRender" />
    </div>
</template>

<script lang="ts">
import {
    computed,
    defineComponent,
    onBeforeMount,
    onBeforeUnmount,
    onMounted,
    watch,
    watchEffect,
} from "vue";
import { useIdle } from "@vueuse/core";
import { RouterLink, RouterView } from "vue-router";
import isNullish from "@/utils/isNullish";
import { useToast } from "primevue/usetoast";
import {
    useFeatureFlag,
    useGoogleUpload,
    useEvents,
    useBrandRedirect,
    useMerchantOfRecordRedirect,
} from "@/composables";
import { useToastStore } from "@/stores/toast";
import { useAuthStore } from "@/stores/userAuth";
import { useSupplierStore } from "@/stores/supplier";
import { useChatListStore } from "@/stores/chatList";
import { useKybStatus } from "@/composables/useKybStatus";
import { useNotificationsStore } from "@/stores/notifications";
import Toast, { type ToastMessageOptions } from "primevue/toast";
import {
    createHotJarTrackingScript,
    createHotJarIdentityScript,
    handleError,
} from "@/utils";
import { uploadEventBus } from "@/buses/uploadEventBus";
import mixpanel from "mixpanel-browser";
import { useZendesk } from "@/composables/useZendesk";

declare global {
    interface Window {
        zE: any;
    }
}

// Mixpanel initialization
const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN;
const IS_PRODUCTION = import.meta.env.VITE_CURRENT_ENV === "PRODUCTION";
mixpanel.init(MIXPANEL_TOKEN, {
    debug: !IS_PRODUCTION,
    // @ts-ignore For some reason this option is still not recognised
    autocapture: {
        pageview: "full-url",
        click: true,
        input: true,
        scroll: true,
        submit: true,
        capture_text_content: true,
    },
    ignore_dnt: true,
    persistence: "localStorage",
    record_sessions_percent: IS_PRODUCTION ? 100 : 0,
    record_mask_text_selector: "",
    loaded: (mixpanelInstance) => {
        const DISABLE_MIXPANEL_TRACKING =
            import.meta.env.VITE_DISABLE_MIXPANEL_TRACKING || false;
        if (DISABLE_MIXPANEL_TRACKING) {
            mixpanelInstance.opt_out_tracking();
        }
    },
});

export default defineComponent({
    components: {
        RouterLink,
        RouterView,
        Toast,
    },
    setup() {
        const authStore = useAuthStore();
        const toastStore = useToastStore();
        const supplierStore = useSupplierStore();
        const notificationsStore = useNotificationsStore();
        const chatListStore = useChatListStore();
        const { initializeFeatureFlags } = useFeatureFlag();
        const { fetchKybStatus } = useKybStatus();
        const { shouldBlockRender: shouldBlockRenderForBrand } =
            useBrandRedirect();
        const { shouldBlockRender: shouldBlockRenderForMor } =
            useMerchantOfRecordRedirect();
        const shouldBlockRender = computed(
            () =>
                shouldBlockRenderForBrand.value || shouldBlockRenderForMor.value
        );

        const {
            connect: connectNotifications,
            disconnect: disconnectNotifications,
        } = notificationsStore;

        const toast = useToast();
        const { resumableUploadWorker } = useGoogleUpload();
        const { emitEvent } = useEvents();

        /**
         * idle period is defined as 10 minutes
         * if the user remains idle for 10 minutes idle will be toggled to true
         */
        const { idle } = useIdle(10 * 60 * 1000);

        const createCookieScript = () => {
            const script = document.createElement("script");
            script.id = "cookieyes";
            script.src =
                "https://cdn-cookieyes.com/client_data/92ba444ef47267a44a5227ff/script.js";
            script.async = true;
            document.head.appendChild(script);
        };

        const createHubspotScript = () => {
            const script = document.createElement("script");
            script.id = "hs-script-loader";
            script.type = "text/javascript";
            script.src = "//js-na1.hs-scripts.com/20114200.js";
            script.async = true;
            document.head.appendChild(script);
        };

        const userUUID = computed(() => authStore.getUserUUID);
        const chatWidgetKey = computed(
            () => authStore.companyData?.zendesk_chat_widget_key
        );
        const companyName = computed(() => authStore.companyData?.name);
        const zendeskToken = computed(
            () => authStore.companyData?.zendesk_chat_token
        );

        const { isFeatureFlagEnabled } = useFeatureFlag();

        const isBuiltInChatHidden = computed(() =>
            isFeatureFlagEnabled("isBuiltInChatHidden")
        );

        const {
            setCompanyField,
            loginToZendesk,
            createZendeskChatWidgetScript,
        } = useZendesk();

        watch(
            [chatWidgetKey, zendeskToken, isBuiltInChatHidden],
            async ([newKey, newToken, isBuiltInChatHiddenValue]) => {
                if (newKey && newToken && isBuiltInChatHiddenValue) {
                    await createZendeskChatWidgetScript();
                    loginToZendesk();
                    setCompanyField(companyName.value);
                }
            },
            { immediate: true }
        );

        toastStore.$subscribe((mutation, state) => {
            toast.add({ ...state } as ToastMessageOptions);
        });

        watch(
            userUUID,
            () => {
                if (!userUUID.value) return;

                chatListStore.fetchUserChatList();
            },
            { immediate: true }
        );

        watchEffect(() => {
            if (authStore.isSupplier && !isNullish(authStore.companyAddress)) {
                supplierStore.fetchSupplierKybPayoutDetails();
            }

            if (authStore.isBrand) {
                fetchKybStatus();
            }

            if (authStore.isMerchantOfRecord) {
                authStore.fetchUserOAuthToken();
            }

            // Tracking logic
            if (authStore.auth && authStore.userData) {
                initializeFeatureFlags();

                if (import.meta.env.VITE_CURRENT_ENV === "PRODUCTION") {
                    // Initialize Pendo Once the user has been authenticated and we are on production servers
                    // @ts-ignore
                    // eslint-disable-next-line no-undef
                    pendo.initialize({
                        visitor: {
                            id: authStore.userData.uuid,
                            email: authStore.userData.email,
                            firstName: authStore.userData.first_name,
                            lastName: authStore.userData.last_name,
                        },

                        account: {
                            id: authStore.companyData.uuid,
                            accountName: authStore.companyData.name,
                        },
                    });
                }
            }
        });

        /**
         * if the user remains idle for more than 10 minutes and
         * the user token is not found in local storage then logout the user
         */
        watch(idle, (idleValue) => {
            const userToken = localStorage.getItem("userAuthToken");
            if (idleValue && !userToken) {
                authStore.signout();
            }
        });

        const unwatchAuthStore = watch(authStore, () => {
            if (!authStore.companyData || !authStore.userData || false) return;

            connectNotifications();
            if (IS_PRODUCTION) {
                createHotJarTrackingScript();
                createHotJarIdentityScript({
                    companyData: authStore.companyData,
                    userData: authStore.userData,
                });
            }
            unwatchAuthStore();
        });

        window.addEventListener("beforeunload", () => {
            disconnectNotifications();
        });

        onBeforeMount(async () => {
            if (import.meta.env.VITE_CURRENT_ENV === "PRODUCTION") {
                createCookieScript();
                createHubspotScript();
            }
        });

        /**
         * Sets up event listeners for the Web Worker
         * Handles upload completion and errors
         */
        onMounted(() => {
            resumableUploadWorker.addEventListener(
                "message",
                async (evt: any) => {
                    if (evt.data.error) {
                        handleError(evt.data.error);
                        return;
                    }

                    const payload = {
                        ...evt.data.params,
                        file_name: evt.data.response.name,
                    };

                    try {
                        await uploadEventBus.executeCallback(
                            evt.data.callbackId,
                            evt.data.modelId,
                            payload
                        );
                    } catch (error) {
                        const displayToast = true;
                        handleError(error, displayToast);
                        return;
                    }

                    if (evt.data.eventName) {
                        emitEvent(evt.data.eventName);
                    }
                }
            );
        });

        onBeforeUnmount(() => {
            disconnectNotifications();
        });

        return {
            authStore,
            shouldBlockRender,
        };
    },
});
</script>
